import { useEffect, useRef, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { ContentCopy, Refresh } from '@mui/icons-material'
import { com } from '@eidu/web'
import useRegistryAssets from '../../io/useRegistryAssets'
import { RegistryAsset } from '../../io/api/registry/listRegistryAssets'
import useRegistryAsset from '../../io/useRegistryAsset'
import PublishableEntityKinds = com.eidu.sharedlib.entity.publish.PublishableEntityKinds

const KIND_OPTIONS = ['', ...Array.from(PublishableEntityKinds.asJsReadonlySetView()).map((k) => k.toString())]

const formatDate = (ms: number) => new Date(ms).toLocaleString()

type SelectedAssetVersionsProps = {
  entityId: string
  onCopyReference: (entityId: string, versionId: string) => void
}

const SelectedAssetVersions = ({ entityId, onCopyReference }: SelectedAssetVersionsProps) => {
  const { detail, isLoading: detailLoading, error: detailError } = useRegistryAsset(entityId)
  if (detailLoading) return <CircularProgress size={20} />
  if (detailError) return <Alert severity="error">{(detailError as Error).message}</Alert>
  if (!detail) return null
  return (
    <List dense>
      {detail.versions.map((v) => (
        <ListItem
          key={v.versionId}
          secondaryAction={
            <Tooltip title="Copy asset reference">
              <IconButton edge="end" onClick={() => onCopyReference(detail.entityId, v.versionId)}>
                <ContentCopy fontSize="small" />
              </IconButton>
            </Tooltip>
          }
        >
          <ListItemText
            primary={`${v.versionId.slice(0, 8)}…`}
            secondary={`${formatDate(v.createdAt)} · ${v.dependencyCount} deps`}
          />
        </ListItem>
      ))}
    </List>
  )
}

const RegistryPage = () => {
  const [search, setSearch] = useState('')
  const [kind, setKind] = useState<string>('')
  const [selected, setSelected] = useState<RegistryAsset | null>(null)
  const [copied, setCopied] = useState<string | null>(null)
  const sentinelRef = useRef<HTMLDivElement | null>(null)

  const { assets, loadMore, reload, hasMore, loading, error } = useRegistryAssets({
    search: search.trim() || undefined,
    kind: kind || undefined,
  })

  useEffect(() => {
    const node = sentinelRef.current
    if (!node) return
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && hasMore && !loading) loadMore()
      },
      { rootMargin: '256px' }
    )
    observer.observe(node)
    return () => observer.disconnect()
  }, [hasMore, loading, loadMore])

  const onCopyReference = async (entityId: string, versionId: string) => {
    const ref = `asset://${entityId}@${versionId}`
    await navigator.clipboard.writeText(ref)
    setCopied(ref)
    window.setTimeout(() => setCopied(null), 1500)
  }

  return (
    <Stack direction="row" spacing={2} sx={{ height: '100%', minHeight: 0, p: 2, overflow: 'hidden' }}>
      <Stack spacing={1} sx={{ flex: 1, minWidth: 0, minHeight: 0 }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
          <TextField
            label="Search"
            size="small"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ flex: 1 }}
          />
          <TextField
            select
            label="Kind"
            size="small"
            value={kind}
            onChange={(e) => setKind(e.target.value)}
            sx={{ width: 160 }}
          >
            {KIND_OPTIONS.map((k) => (
              <MenuItem key={k} value={k}>
                {k || 'Any'}
              </MenuItem>
            ))}
          </TextField>
          <Tooltip title="Reload">
            <IconButton onClick={reload}>
              <Refresh />
            </IconButton>
          </Tooltip>
        </Stack>
        {error && <Alert severity="error">{error.message}</Alert>}
        <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', border: 1, borderColor: 'divider', borderRadius: 1 }}>
          <List dense>
            {assets.map((a) => (
              <ListItemButton
                key={a.entityId}
                selected={selected?.entityId === a.entityId}
                onClick={() => setSelected(a)}
              >
                <ListItemText
                  primary={
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="body2">{a.name}</Typography>
                      <Chip label={a.kind} size="small" />
                    </Stack>
                  }
                  secondary={`Org ${a.publishingOrgId.slice(0, 8)}… · first published ${formatDate(a.firstPublishedAt)}`}
                />
              </ListItemButton>
            ))}
            <div ref={sentinelRef} />
            {loading && (
              <ListItem>
                <CircularProgress size={20} />
              </ListItem>
            )}
            {!loading && assets.length === 0 && (
              <ListItem>
                <ListItemText secondary="No published assets." />
              </ListItem>
            )}
          </List>
        </Box>
      </Stack>
      <Divider orientation="vertical" flexItem />
      <Stack spacing={1} sx={{ width: 360, minHeight: 0 }}>
        {selected ? (
          <>
            <Typography variant="h6" sx={{ flexShrink: 0 }}>
              {selected.name}
            </Typography>
            <Typography variant="caption" sx={{ flexShrink: 0 }}>
              Entity {selected.entityId}
            </Typography>
            <Typography variant="caption" sx={{ flexShrink: 0 }}>
              Log {selected.logId}
            </Typography>
            <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
              <SelectedAssetVersions entityId={selected.entityId} onCopyReference={onCopyReference} />
            </Box>
          </>
        ) : (
          <Typography variant="body2" color="text.secondary">
            Select an asset to view its versions and copy a reference.
          </Typography>
        )}
        {copied && (
          <Alert severity="success" sx={{ flexShrink: 0 }}>
            Copied {copied}
          </Alert>
        )}
        <Button onClick={() => setSelected(null)} disabled={!selected} sx={{ flexShrink: 0 }}>
          Clear selection
        </Button>
      </Stack>
    </Stack>
  )
}

export default RegistryPage
